import React, { useState, useEffect } from 'react'
import styled, { withTheme } from 'styled-components'

import { SEARCH_VIEW_CATEGORY_LIST_ID } from '../../plugins/online/homeView/constants.js'
import findByLayoutId from '../../utils/findByLayoutId.js'
import { Button } from '../ui/buttons/Button.jsx'
import { ColoredIcon } from '../ui/icons/Icon.jsx'
import ToolTip from '../ui/tooltips/ToolTip.jsx'
import ToolTipComponent from '../ui/tooltips/ToolTipComponent.jsx'

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-areas:
    "header"
    "app";
  grid-template-rows: auto 1fr;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 10;
  overflow: hidden;
`

// Main is where the map is rendered
const Main = styled.div`
  grid-area: main;
  height: 100%;
  z-index: 50;
  position: relative;
`

const FloatingComponent = styled.div`
  position: absolute;
  z-index: 150;
  height: auto;
  max-height: 100%;
  width: 100%;
`

const SidebarWrapper = styled(FloatingComponent)`
  display: flex;
  flex-direction: column;
  width: auto;
  height: auto;
  transition: transform 0.5s ease-in-out;
  z-index: 152;

  .-desktop & {
    box-shadow: none;
    max-height: 100%;
    padding: 20px 20px 42px;
    left: 0;
  }

  .-mobile & {
    height: ${({ isFullscreen }) => isFullscreen ? '100%' : 'auto'};
    width: 100%;
  }
`

const Sidebar = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  min-height: 0;

  .-desktop & {
    width: ${({ isWide }) => isWide ? '375px' : '320px'};
    max-height: calc(100% - 60px);
  }

  .-mobile & {
    width: 100%;
    max-height: 100%;
    height: ${({ isFullscreen }) => isFullscreen ? '100%' : 'auto'};

    /* Avoid Chrome to see Safari hack */
    @supports (-webkit-touch-callout: none) {
        /* The hack for Safari */
        max-height: -webkit-fill-available;
    }
  }
`

const Header = styled.div`
  grid-area: header;
  background-color: ${props => props.theme.colors.widgetBackground};
  border-bottom: ${({ isContentOpen }) => isContentOpen ? '1px solid #ebebeb' : '1px solid white'};
  z-index: 2;
  display: flex;
`
const Content = styled.div`
  height: auto;
  background-color: ${({ themeColor }) => themeColor};
  overflow: hidden;
  transition: 0.5s ease-in-out;
  display: flex;
  flex-direction: column;
  z-index: 1;

  .-mobile & {
    height: 100%;
  }
`
const ContentToggler = styled(Button)`
  display: none;

  .-desktop & {
    display: block;
    margin: auto;
    background-color: ${props => props.theme.colors.mapNavBackground};
    color:  ${props => props.theme.colors.mapNavText};
    padding: 0 12px;
    border: none;
    border-radius: unset;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    position: relative;

    ${ColoredIcon} span {
      transform: ${({ isOpen }) => isOpen ? 'none' : 'rotate(180deg)'};
    }
  }

  &:focus-visible {
    outline: 2px solid darkorange;
    outline-offset: 2px;
  }
`

const BottomBar = styled(FloatingComponent)`
  bottom: 0;
  transition: 0.2s ease-in-out height;
  box-sizing: border-box;
  display: contents;
`

const ControlsWrapper = styled(FloatingComponent)`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: auto;
  right: 0;
  align-items: flex-end;
  justify-content: flex-end;
`

const ButtonOptionsWrapper = styled.div`
  flex: 0 0 auto;
  margin-left: auto;
  pointer-events: all;
  bottom: 0;
  right: 0;
  padding: 20px;
  width: unset;
  display: ${({ isHidden }) => isHidden ? 'none' : 'flex'};
  flex-direction: column;
  min-height: 1px;
`

const MapControlWrapper = styled.div`
  display: inline-flex;

  &:not(:first-child) {
    margin-top: 8px;
  }
`
const LevelSelector = styled.div`
  ${({ isHidden }) => isHidden ? 'display: none;' : 'display: flex;'}
  ${({ isMobile }) => isMobile ? 'display: none;' : 'display: flex;'}
  align-items: flex-start;
  min-height: 1px;
  flex: 1 1 auto;
  pointer-events: none;
  padding: 20px 20px 0 0;
`

const MobileLevelSelector = styled(FloatingComponent)`
  pointer-events: none;
  height: 100%;
  min-height: 1px;
  flex: 1 1 auto;
  display: flex;
  align-items: flex-end;
`

const FloatingBannerWrapper = styled.div`
  display: flex;
  justify-content: center;

  .locationButton {
    height: 56px;
    width: 56px;
    outline: none;
    border: none;
    background: #FFFFFF;
    position: absolute;
    bottom: 88px;
    right: 20px;
    border-radius: 100%;
  }
`

// z-index value is 151 so it sits above the map
const MapOverlay = styled.div`
  z-index: 151;
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
`

const TopHeader = styled.div`
  grid-area: header;
  z-index: 160;
  position: relative;
  width: 100%;
`

const MapWrapper = styled.div`
  grid-area: app;
  position: relative;
  display: grid;
  grid-template-columns: 100%;
  grid-template-areas:
    "main";
  grid-template-rows: 1fr;
  height: 100%;
  width: 100%;
  z-index: 10;
  overflow: hidden;
`

const MobileToastWrapper = styled.div`
  grid-area: 'main';
  position: absolute;
  bottom: 0;
  pointer-events: all;
  padding: 26px;
  width: 100%;
  min-height: 30px;
  padding-bottom: 40px;
  display: flex;
  justify-content: center;
  background-color: transparent;
`

const MobileMapControlWrapper = styled.div`
  display: inline-flex;

  &:not(:first-child) {
    margin-top: 12px;
  }
`

const MapCover = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 151;
  background-color: lightgray;
  pointer-events: none;
  font-size: 80px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
`

const FullscreenOnlyLayout = withTheme(({ app, children, theme, uiHide, Loading, mapLoaded }) => {
  const [isMapDisabled, setIsMapDisabled] = useState(false)
  const [isHeaderVisible, setIsHeaderVisible] = useState(true)
  const [isSearchConfirmed, setIsSearchConfirmed] = useState(false)
  const T = app.gt()
  const [isMobile, setIsMobile] = useState(app.env.isMobile())
  const isDesktop = !isMobile
  const [hideMapForVisualTest, setHideMapForVisualTest] = useState(false)

  // Make sure this is the first layer shown when we mount
  useEffect(() => {
    app.bus.send('layer/show', { id: SEARCH_VIEW_CATEGORY_LIST_ID })
  }, [])

  useEffect(() => {
    const unsub = app.bus.on('map/disableMap', async ({ disable = true }) => {
      if (isMapDisabled !== disable) {
        setIsMapDisabled(disable)
      }
    })
    return unsub
  })

  useEffect(() => {
    const unsub = app.bus.on('map/visualTest', ({ hideMap }) => setHideMapForVisualTest(hideMap))
    return unsub
  })

  useEffect(() => {
    const unsub = app.bus.on('screen/displayHeader', async ({ display = true }) => {
      if (isHeaderVisible !== display) {
        setIsHeaderVisible(display)
      }
    })
    return unsub
  })

  useEffect(() => {
    app.bus.monitor('headerOnline/updateSearchConfirmed', ({ isSearchConfirmed }) => setIsSearchConfirmed(isSearchConfirmed))
  })

  useEffect(() => {
    const unsub = app.bus.on('env/resize', async () => {
      if (app.env.isMobile() && isDesktop) {
        setIsMobile(true)
        await toMobile()
      }
      if (app.env.isDesktop() && isMobile) {
        setIsMobile(false)
        await toDesktop()
      }
    })
    return unsub
  })

  const getLayersShowing = async () => {
    const visibleLayers = (await app.bus.send('layers/getVisibleLayersIds'))[0]
    const isPoiShowing = visibleLayers.includes('poiViewMobile') || visibleLayers.includes('poiViewDesktop')
    const isNavShowing = visibleLayers.includes('DirectionsSearchControls') || visibleLayers.includes('DirectionsSearchView') || visibleLayers.includes('DirectionsResultControls') || visibleLayers.includes('DirectionsResultMobile')
    const isFlightStatusShowing = visibleLayers.includes('flightSearchView')
    return { isPoiShowing, isNavShowing, isFlightStatusShowing }
  }

  const toDesktop = async () => {
    const { isPoiShowing, isNavShowing, isFlightStatusShowing } = await getLayersShowing()
    if (!isPoiShowing && !isNavShowing && !isFlightStatusShowing) {
      app.bus.send('layers/showWithLayoutId', 'content')
      app.bus.send('headerOnline/hide')
      if (!isSearchConfirmed) { // restore custom actions if not searching
        const [contentLayers] = await app.bus.send('layers/getVisibleLayersByLayoutId', 'content')
        const [previousLayer] = await app.bus.send('layers/getPreviousLayer')
        // default to show category list if there is no previous layer
        app.bus.send('layers/show', { id: previousLayer || SEARCH_VIEW_CATEGORY_LIST_ID })
        app.bus.send('layers/setPreviousLayer', contentLayers[0] || '')
      } else {
        app.bus.send('layers/show', { id: 'SearchResultsBody' })
      }
      app.bus.send('layers/show', { id: 'headerOnlineHome' })
    }
  }

  const toMobile = async () => {
    const { isPoiShowing, isNavShowing, isFlightStatusShowing } = await getLayersShowing()
    if (!isPoiShowing && !isNavShowing && !isFlightStatusShowing) {
      if (!isSearchConfirmed) {
        const [contentLayers] = await app.bus.send('layers/getVisibleLayersByLayoutId', 'content')
        app.bus.send('layers/hideWithLayoutId', 'content')
        app.bus.send('layers/setPreviousLayer', contentLayers[0] || '')
        app.bus.send('headerOnline/show')
      } else {
        app.bus.send('layers/show', { id: 'SearchResultsHeader' })
      }
    }
  }

  const toggleContent = async () => {
    const [contentLayers] = await app.bus.send('layers/getVisibleLayersByLayoutId', 'content')

    if (content.length > 0) {
      app.bus.send('layers/hideWithLayoutId', 'content')
    } else {
      const [previousLayer] = await app.bus.send('layers/getPreviousLayer')
      // default to show category list if there is no previous layer
      app.bus.send('layers/show', { id: previousLayer || SEARCH_VIEW_CATEGORY_LIST_ID })
    }

    app.bus.send('layers/setPreviousLayer', contentLayers[0] || '')
  }

  const topHeader = findByLayoutId('topHeader', children, app) // These are arrays of components
  const map = findByLayoutId('map', children, app)
  const appInfo = findByLayoutId('appInfo', children, app) // this is separated from the map, so that it can be placed later in the DOM for accessibilty
  const content = findByLayoutId('content', children, app)
  const header = findByLayoutId('headerOnline', children, app)
  const floatingBanner = findByLayoutId('floatingBanner', children, app)
  const bottomBar = findByLayoutId('bottomBar', children, app)
  const buttonOptions = findByLayoutId('buttonOptions', children, app)
  const mobileLevelSelector = findByLayoutId('mobileLevelSelector', children, app)
  const modalWindow = findByLayoutId('modalWindow', children, app)
  const mapControls = findByLayoutId('mapControls', children, app)
  const toast = findByLayoutId('toast', children, app)
  const dialog = findByLayoutId('dialog', children, app)
  const feedbackZone = findByLayoutId('feedbackZone', children, app)

  const isContentVisible = content.length > 0

  const levelSelectorDesktop = mapControls.filter(el => isDesktop && (el.key === 'widget-mapLevelSelectorOnline'))

  const isPresentational = content.filter(el => el.key.startsWith('widget-poiView') || el.key.startsWith('widget-flightDetails')).length > 0 || false

  const isContentFullscreen = isMobile && content.filter(el => el.key === 'widget-SearchResultsBody' || el.key === 'widget-SearchResultsList' || el.key === 'widget-DirectionsSearchView' || el.key === 'widget-SearchResultsCategoryList').length > 0

  const shouldHideSidebar = () => uiHide && ((isDesktop && uiHide.sidebar) || (!isDesktop && uiHide.mobileSearchBar))

  return (
    <Loading hasLoaded={mapLoaded}>
      <Wrapper className={isDesktop ? '-desktop' : '-mobile' }>
        <TopHeader>{topHeader}</TopHeader>
        <MapWrapper>
          {dialog}
          {isDesktop && toast}
          { isMobile && (
            <MobileToastWrapper>
              {toast}
            </MobileToastWrapper>
          )}
          <Main aria-label={T('fullscreenOnlyLayout:Map · Use arrow keys to pan the map.')} role='application'>
            {hideMapForVisualTest && <MapCover>visual test map cover</MapCover>}
            {map}
          </Main>
          {mapLoaded && <>
            { !shouldHideSidebar() &&
            <SidebarWrapper
              aria-label={T('fullscreenOnlyLayout:Search Wayfinder on Web')}
              role='navigation'
              className="sidebar-wrapper" isHidden={uiHide.sidebar} isFullscreen={isContentFullscreen}>
              <Sidebar isWide={isPresentational} isFullscreen={isContentFullscreen}>
                {isHeaderVisible && <Header isContentOpen={isContentVisible}>
                  {header}
                </Header>}
                <Content themeColor={theme.colors.background}>
                  {content}
                </Content>
              </Sidebar>
              {!isPresentational
                ? <ToolTipComponent tooltipText={isContentVisible ? T('fullscreenOnlyLayout:Collapse Panel') : T('fullscreenOnlyLayout:Expand Panel')} arrowPosition="top">
                  <ContentToggler aria-label={isContentVisible ? T('fullscreenOnlyLayout:Collapse Panel') : T('fullscreenOnlyLayout:Expand Panel')} onClick={() => toggleContent()} isOpen={isContentVisible} data-cy='ContentToggler'>
                    <ColoredIcon id="chevron-up" width={24} height={24} fillColor={theme.colors.mapNavText} />
                  </ContentToggler>
                </ToolTipComponent>
                : null}
              {isMobile
                ? (
                  <FloatingBannerWrapper>
                    {floatingBanner}
                  </FloatingBannerWrapper>)
                : <div />
              }
            </SidebarWrapper> }
            {isMapDisabled && <MapOverlay />}
            <BottomBar >
              {bottomBar}
            </BottomBar>
            <MobileLevelSelector>{mobileLevelSelector}</MobileLevelSelector>
            <ControlsWrapper>
              <LevelSelector aria-label={T('fullscreenOnlyLayout:Level selector dropdown')} isHidden={uiHide.levelSelector} isMobile={isMobile}>{levelSelectorDesktop}</LevelSelector>
              <ButtonOptionsWrapper isHidden={uiHide.controls}>
                {isMobile && <>
                  {
                    mapControls.map((el, index) => <MobileMapControlWrapper key={index}>
                      {el}
                    </MobileMapControlWrapper>)
                  }
                </>}
                {buttonOptions.map((el, key) => <MapControlWrapper key={key}>
                  {el}
                </MapControlWrapper>)}

                {isDesktop && feedbackZone}
              </ButtonOptionsWrapper>
            </ControlsWrapper>
          </>}
          {appInfo}
          {isMobile && feedbackZone}
        </MapWrapper>
        <ToolTip id="tooltip" />
        {modalWindow}
      </Wrapper>
    </Loading>)
})

export default withTheme(FullscreenOnlyLayout)
